import { Target01OutlineIcon, WarningColoredIcon } from '@carboncopyco/icons';
import type { TourFrame, TourStep } from '@carboncopyco/types';
import {
  ColorHex,
  TextBase,
  Tooltip,
  TooltipBubble,
  TooltipMessage,
} from '@carboncopyco/ui';
import classNames from 'classnames';
import type React from 'react';
import { useEffect, useRef } from 'react';
import { useTourDispatch, useTourSelector } from '../../../../../../hooks';
import usePrevious from '../../../../../../hooks/usePrevious';
import { selectActiveStepId, setActiveStepId } from '../../../../../../store';
import { getStepWarningActive } from '../../../../../../utils/getPositionWarning';
import { useTourEditorPageProps } from '../../../../TourEditorPageContext';
import type { ReplayerEditorRef } from '../../../../ToursReplayerEditor';
import { IconWrapper } from '../IconWrapper';
import useFrameWarnings from '../useFrameWarnings';
import Dropdown from './Dropdown';
import useStepActions from './useStepActions';

interface IStep {
  tourId: number;
  step: TourStep;
  frame: TourFrame;
  frames: TourFrame[];
  replayerRef?: React.RefObject<ReplayerEditorRef>;
  isStepDisabled: boolean;
  addStep?: boolean;
  selected?: boolean;
  onStepSelect: () => void;
  doReplay: (step: TourStep) => void;
}

export const Step = ({
  tourId,
  frame,
  frames,
  replayerRef,
  step,
  isStepDisabled,
  selected,
  doReplay,
  onStepSelect,
}: IStep) => {
  const { title, stepId } = step;
  const { uuid } = useTourEditorPageProps();
  const activeStepId = useTourSelector(selectActiveStepId(uuid));
  const dispatch = useTourDispatch();

  const { framesWarnings } = useFrameWarnings();

  const isPositionWarningActive = getStepWarningActive(
    framesWarnings,
    frame.id,
    step.stepId,
    'position'
  );

  const prevShowTooltip = usePrevious(step.showTooltip);

  useEffect(() => {
    if (stepId !== activeStepId) {
      return;
    }

    if (prevShowTooltip !== undefined && prevShowTooltip !== step.showTooltip) {
      replayerRef.current.refreshTooltip(step);
    }
  }, [prevShowTooltip, step.showTooltip]);

  const warningTooltipRef = useRef();

  const {
    handleStepClick,
    handleDeleteStep,
    handleAddStepAbove,
    handleAddStepBellow,
  } = useStepActions({
    stepId,
    tourId,
    frame,
    frames,
    replayerRef,
    doReplay,
    onStepSelect,
  });

  const handleDropdownButtonClick = () => {
    dispatch(setActiveStepId({ uuid, stepId }));
  };

  const disabledStepTitleColor = selected
    ? 'text-ds-primary-300'
    : 'text-ds-natural-200';

  const activeStepTitleColor = selected
    ? 'text-ds-primary-300'
    : 'text-ds-natural-600';

  const titleColor = isStepDisabled
    ? disabledStepTitleColor
    : activeStepTitleColor;

  const disabledStepIconColor = selected
    ? ColorHex.primary300
    : ColorHex.natural200;

  const activeStepIconColor = selected
    ? ColorHex.primary300
    : ColorHex.natural600;

  const iconColor = isStepDisabled
    ? disabledStepIconColor
    : activeStepIconColor;

  return (
    <div
      data-testid="drawer-frames-step-item"
      className={classNames(
        `group my-1 w-full h-[42px] border rounded-md flex items-center py-1 px-2.5 justify-between
        font-sm`,
        {
          'border-ds-primary-300 drop-shadow-[0_0_1.5px_#0078EF] bg-ds-neutral-50':
            selected,
          'bg-white': !isStepDisabled,
          'bg-ds-natural-50': isStepDisabled,
          'py-3': isStepDisabled,
        }
      )}
      onClick={() => {
        handleStepClick(step);
      }}
    >
      <div className="flex w-full overflow-hidden items-center">
        <IconWrapper size="sm">
          <Target01OutlineIcon
            size={20}
            color={iconColor}
          />
        </IconWrapper>

        <div
          className={classNames('pl-2', {
            'text-ds-natural-200': isStepDisabled && !selected,
            'text-ds-primary-300':
              (!isStepDisabled && selected) || (isStepDisabled && selected),
            'text-ds-natural-600': !isStepDisabled && !selected,
          })}
        >
          <TooltipBubble
            placement="top"
            title={title}
          >
            <div className="w-ds-46 truncate">
              <TextBase
                label={title}
                ellipsis
                color={titleColor}
              />
            </div>
          </TooltipBubble>
        </div>
      </div>

      {isPositionWarningActive && (
        <>
          <div ref={warningTooltipRef}>
            <IconWrapper size="sm">
              <WarningColoredIcon size={16} />
            </IconWrapper>
          </div>

          <Tooltip
            placement="top"
            anchorRef={warningTooltipRef}
            noArrow
          >
            <TooltipMessage title="Resolve 1 issue on this step" />
          </Tooltip>
        </>
      )}
      {!isStepDisabled && (
        <Dropdown
          onDropdownButtonClick={handleDropdownButtonClick}
          onAddStepAboveClick={handleAddStepAbove}
          onAddStepBellowClick={handleAddStepBellow}
          onDeleteClick={handleDeleteStep}
        />
      )}
    </div>
  );
};
